import { useAuth } from "../../controllers/AuthProvider";
import { isAdmin, type Principal } from "../../controllers/oidc";
import type { ShellView } from "../../controllers/useProjectNav";
import type { Workspace } from "../../models/account";
import compathMark from "../../assets/compath-mark.svg";

export function TitleBar({
  principal, crumb, onToggleNav, workspaces, currentWorkspaceId, onSelectWorkspace, onOpenCreateWorkspace,
  shellView, onShellView,
}: {
  principal: Principal;
  crumb: string;
  onToggleNav: () => void;
  workspaces: Workspace[];
  currentWorkspaceId: string | null;
  onSelectWorkspace: (id: string) => void;
  /** Opens the full workspace-registration dialog. */
  onOpenCreateWorkspace: () => void;
  /** Which shell view is active (null while a project leaf is open) — highlights the top menu. */
  shellView: ShellView | null;
  onShellView: (v: ShellView) => void;
}) {
  const { logout } = useAuth();
  return (
    <header className="titlebar">
      <button className="nav-toggle" title="사이드바" onClick={onToggleNav}>☰</button>
      {/* Product mark only — the company logo sits at the foot of the left rail. */}
      <span className="tb-brand">
        <img className="tb-mark" src={compathMark} alt="" aria-hidden />
        <span className="wordmark">compath</span>
      </span>

      {/* Top menu: the destinations that belong to NO single project — the landing roll-up, the app
          marketplace, the download centre. The left rail is project-centric, so these live up here. */}
      <nav className="tb-menu" aria-label="상단 메뉴">
        <button
          className={`tb-menu-btn ${shellView === "home" ? "on" : ""}`}
          aria-current={shellView === "home" ? "page" : undefined}
          onClick={() => onShellView("home")}
        >
          <span className="tb-menu-ico" aria-hidden>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M3 10.5 12 3l9 7.5"/><path d="M5.5 9.5V20h13V9.5"/></svg>
          </span> 홈
        </button>
        <button
          className={`tb-menu-btn ${shellView === "apps" ? "on" : ""}`}
          aria-current={shellView === "apps" ? "page" : undefined}
          onClick={() => onShellView("apps")}
        >
          <span className="tb-menu-ico" aria-hidden>▦</span> 앱
        </button>
        <button
          className={`tb-menu-btn ${shellView === "downloads" ? "on" : ""}`}
          aria-current={shellView === "downloads" ? "page" : undefined}
          onClick={() => onShellView("downloads")}
        >
          <span className="tb-menu-ico" aria-hidden>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M12 3v12"/><path d="m7 11 5 5 5-5"/><path d="M4 20h16"/></svg>
          </span> 다운로드
        </button>
      </nav>

      {/* Workspace switcher + add — the single place a workspace is chosen or created. */}
      <select
        className="ws-switcher"
        value={currentWorkspaceId ?? ""}
        onChange={(e) => onSelectWorkspace(e.target.value)}
        aria-label="워크스페이스 선택"
      >
        {workspaces.length === 0 && <option value="">워크스페이스 없음</option>}
        {workspaces.map((w) => (
          <option key={w.id} value={w.id}>{w.name}</option>
        ))}
      </select>
      <button
        className="ws-add-btn"
        title="워크스페이스 추가"
        aria-label="워크스페이스 추가"
        onClick={onOpenCreateWorkspace}
      >＋</button>

      <span className="crumb">／ {crumb}</span>

      {/* Current account + logout — pinned to the right. */}
      <div className="tb-user">
        <span className="avatar">{principal.name.slice(0, 1)}</span>
        <span className="tb-user-name">{principal.name}</span>
        {isAdmin(principal) && <span className="badge locked" style={{ marginLeft: 2 }}>관리자</span>}
        <button className="link-btn" onClick={logout}>로그아웃</button>
      </div>
    </header>
  );
}
